<script setup lang="ts">
import AudioVideoSettings from '@jaaluu/components/Settings/AudioVideoSettings.vue';
import LangSettings from '@jaaluu/components/Settings/LangSettings.vue';
import ThemeSettings from '@jaaluu/components/Settings/ThemeSettings.vue';
import SideMenu from '@jaaluu/components/SideMenu/SideMenu.vue';
import TeacherChat from '@jaaluu/components/TeacherChat.vue';
import { GAnalyticsEvent } from '@jaaluu/interfaces/GAnalyticEvent';
import stores from '@jaaluu/stores';
import vuetify from '@jaaluu/vuetify';
import { t } from '@shared/i18n';
import { CategoryType, LanguageLevel } from '@shared/openapi/jaaluu-api';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FavoriteUrls from '@jaaluu/views/Favorites/FavoriteUrls.vue';
import FavoriteWordmaps from '@jaaluu/views/Favorites/FavoriteWordmaps.vue';
import FavoriteWords from '@jaaluu/views/Favorites/FavoriteWords.vue';
import CourseChapterInfo from './CourseChapterInfo.vue';
import CourseMenu from './CourseMenu.vue';
import Dialog from './Dialog.vue';
import LevelSettings from './Settings/LevelSettings.vue';
import TextFieldWithButton from './TextFieldWithButton.vue';

const { langForeign, langOptions, langLevel, langNative } = stores.language.state;
const { logoBall, logoWide } = stores.assets.state;
const { courseStatus } = stores.course.state;
const { inFullscreen } = stores.settings.state;
const { globalSearch } = stores.search.state;
const { allFavWordsLang, allFavUrlsLang, allFavWordmapsLang } = stores.favorites.state;
const { sendEvent } = stores.gAnalytics.funcs;

const router = useRouter();
const route = useRoute();

const routeToHome = () => {
  courseStatus.value.isActive = false;
  router.push(`/${langForeign.value ? langForeign.value : 'welcome'}`);
};

const routeToSearch = (search: string) => {
  console.log('search', search);
  globalSearch.value = search;
  courseStatus.value.isActive = false;
  if (!langForeign.value) return;
  router.push(`/${langForeign.value}/search`);
};

const courseMenuDialogOpen = ref(false);
const teacherChatIsOpen = ref(false);
const favWordsDialogOpen = ref(false);
const favWordsTab = ref('default');
const favUrlsDialogOpen = ref(false);
const favWordmapsDialogOpen = ref(false);

const openCourseMenu = () => {
  courseMenuDialogOpen.value = true;
};

const openUrl = (url: string) => {
  router.push(url);
};

const openSavedWords = () => {
  sendEvent(GAnalyticsEvent.OPEN_FAVORITE_WORDS);
  if (route.name === 'course') {
    favWordsTab.value = 'course';
  } else if (
    route.name === 'category-details' &&
    [CategoryType.PHRASES, CategoryType.SAYINGS].includes(route.params.categoryType as CategoryType)
  ) {
    favWordsTab.value = route.params.categoryType as string;
  } else {
    favWordsTab.value = 'default';
  }
  favWordsDialogOpen.value = true;
};
</script>

<template>
  <v-app-bar app class="w-full appbar" v-if="!inFullscreen && langLevel">
    <div class="d-flex mx-auto max-width align-center justify-space-between px-2">
      <div class="grab d-flex align-center">
        <v-img
          @click="routeToHome"
          transition="scale-transition"
          v-ripple
          v-if="vuetify.display.smAndDown.value || courseStatus.isActive"
          alt="Jaaluu"
          class="mt-1 cursor-pointer mr-2"
          contain
          :src="logoBall"
          width="35"
        />
        <v-img
          @click="routeToHome"
          transition="scale-transition"
          alt="Jaaluu"
          v-else
          v-ripple
          class="mt-1 cursor-pointer d-md-block d-none"
          contain
          width="170"
          :src="logoWide"
        />
        <v-btn class="pa-0 pa-sm-2" v-if="[LanguageLevel.A1_A2].includes(langLevel)">
          <CourseChapterInfo class="max-button" @click="() => openCourseMenu()" />
        </v-btn>
      </div>

      <div
        v-if="route.name !== 'search' && langLevel !== LanguageLevel.A1_A2"
        class="d-flex align-center"
      >
        <TextFieldWithButton
          clearable
          hide-details
          class="mt-1 pr-4"
          prepend-inner-icon="mdi-magnify"
          @search="routeToSearch"
          :defaultWidth="vuetify.display.smAndDown.value ? 200 : 250"
        >
          <div class="">
            <div v-if="langOptions && langNative && langForeign" class="d-flex align-center">
              <div>{{ t('general.what_to_read') }}</div>
            </div>
          </div>
        </TextFieldWithButton>
      </div>
      <div class="d-flex align-center">
        <v-btn
          @click="
            () => {
              sendEvent(GAnalyticsEvent.OPEN_LANGUAGE_TEACHER, { source: 'app_bar' });
              teacherChatIsOpen = true;
            }
          "
          color="grey"
          size="x-small"
          icon
          class="mr-1"
        >
          <v-icon size="22" icon="mdi-robot" />
        </v-btn>
        <v-btn
          @click="openCourseMenu"
          v-if="courseStatus.isActive && [LanguageLevel.A1_A2].includes(langLevel)"
          size="small"
          variant="text"
          icon="mdi-book-open-variant"
        >
        </v-btn>
        <div>
          <v-menu
            density="compact"
            v-if="allFavWordmapsLang.length > 0 || allFavUrlsLang.length > 0"
          >
            <template v-slot:activator="{ props }">
              <v-badge
                :class="{
                  opac:
                    allFavWordsLang.length === 0 &&
                    allFavUrlsLang.length === 0 &&
                    allFavWordmapsLang.length === 0
                }"
                id="fav-badge"
                offset-x="3"
                offset-y="3"
                color="green"
                v-bind="props"
                :content="
                  allFavWordsLang.length + allFavUrlsLang.length + allFavWordmapsLang.length
                "
              >
                <v-btn color="grey" :size="35" icon> <v-icon size="22" icon="mdi-star" /></v-btn>
              </v-badge>
            </template>
            <v-list>
              <v-list-item @click="() => openSavedWords()" v-if="allFavWordsLang.length > 0">
                <v-list-item-title class="py-2 pr-12">
                  <v-badge
                    offset-x="-15"
                    offset-y="-5"
                    color="green"
                    :content="allFavWordsLang.length"
                  >
                    {{ t('general.words') }}</v-badge
                  ></v-list-item-title
                >
              </v-list-item>
              <v-list-item
                @click="() => (favWordmapsDialogOpen = true)"
                v-if="allFavWordmapsLang.length > 0"
              >
                <v-list-item-title class="py-2 pr-12">
                  <v-badge
                    offset-x="-15"
                    offset-y="-5"
                    color="green"
                    :content="allFavWordmapsLang.length"
                  >
                    {{ t('favorites.wordmaps') }}</v-badge
                  ></v-list-item-title
                >
              </v-list-item>
              <v-list-item
                @click="() => (favUrlsDialogOpen = true)"
                v-if="allFavUrlsLang.length > 0"
              >
                <v-list-item-title class="py-2 pr-12">
                  <v-badge
                    offset-x="-15"
                    offset-y="-5"
                    color="green"
                    :content="allFavUrlsLang.length"
                  >
                    {{ t('favorites.texts') }}</v-badge
                  ></v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>
          <v-badge
            v-else-if="allFavWordsLang.length > 0"
            @click="() => openSavedWords()"
            id="fav-badge"
            offset-x="3"
            offset-y="3"
            color="green"
            :content="allFavWordsLang.length"
          >
            <v-btn color="grey" :size="35" icon> <v-icon size="22" icon="mdi-star" /></v-btn>
          </v-badge>
        </div>
        <SideMenu />
      </div>
      <LangSettings />
      <LevelSettings />
      <ThemeSettings />
      <AudioVideoSettings />
    </div>

    <Dialog
      v-model="courseMenuDialogOpen"
      @close="() => (courseMenuDialogOpen = false)"
      :title="'Curriculum'"
      :max-width="700"
      :fullscreen="vuetify.display.smAndDown.value"
    >
      <CourseMenu @close="() => (courseMenuDialogOpen = false)" />
    </Dialog>

    <Dialog
      v-model="teacherChatIsOpen"
      :title="t('chat.language_teacher')"
      :max-width="900"
      :fullscreen="vuetify.display.smAndDown.value"
      @close="() => (teacherChatIsOpen = false)"
    >
      <TeacherChat v-if="teacherChatIsOpen" />
    </Dialog>

    <Dialog
      v-model="favWordmapsDialogOpen"
      :title="t('general.wordmap')"
      :fullscreen="true"
      :icon="'mdi-star'"
      @close="() => (favWordmapsDialogOpen = false)"
    >
      <FavoriteWordmaps v-if="favWordmapsDialogOpen" />
    </Dialog>

    <Dialog
      v-model="favUrlsDialogOpen"
      :title="t('favorites.texts')"
      :fullscreen="true"
      :icon="'mdi-star'"
      @close="() => (favUrlsDialogOpen = false)"
    >
      <FavoriteUrls v-if="favUrlsDialogOpen" />
    </Dialog>

    <Dialog
      v-model="favWordsDialogOpen"
      :title="t('general.words')"
      :fullscreen="true"
      :icon="'mdi-star'"
      @close="() => (favWordsDialogOpen = false)"
    >
      <FavoriteWords v-if="favWordsDialogOpen" :initial-tab="favWordsTab" @close="() => favWordsDialogOpen = false" />
    </Dialog>
  </v-app-bar>
</template>

<style scoped lang="scss">
.v-theme--darkTheme {
  .appbar {
    background-color: rgb(var(--v-theme-background-darken-1));
  }
}
.flag-wrapper {
  height: 7px;
}
.max-button {
  max-width: 170px;
}
@media screen and (min-width: 600px) {
  .max-button {
    max-width: 400px;
  }
}
</style>
